import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import './FixedOpsScoreboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import { utcDateKey, utcMonthKey, localDateKey, localMonthKey } from '../utils/dateWindow';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

function formatNumber(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return abbreviateNumber(Number(value));
}

function calculatePercent(actual, target) {
  if (!target || target === 0) return 0;
  return Math.round((actual / target) * 100);
}

function ScoreboardDropdown({ options, value, onChange }) {
  return (
    <select className="scoreboard-dropdown" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Compute fiscal meta from dealership settings
function getFiscalMetaForDealership(dealership) {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date();
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) {
    fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  }
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
}

// Get monthly target value from monthlyTargets array for current month
function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  // currentMonth is 0-based (0 = January), monthlyTargets uses 1-based (1 = January)
  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;

  return Number(monthData[field]) || 0;
}

// Convert a target stored at some other frequency into a month's worth
function convertTargetValue(targets, field, fiscalMeta) {
  if (!targets) return 0;

  const base = Number(targets[field]) || 0;
  const storedFreq = (targets.fixedOpsFrequency || 'monthly').toLowerCase();

  let yearly;
  switch (storedFreq) {
    case 'daily':
      yearly = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearly = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearly = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearly = base;
  }

  return Math.round(yearly / fiscalMeta.monthsInYear);
}

// Get the MTD target for a field, preferring monthly targets.
// referenceDate = the selected date, so looking back at a past month shows that
// month's target instead of the current calendar month.
function getMTDTarget(targets, field, dealership, referenceDate = new Date()) {
  const now = new Date(referenceDate);
  const currentMonth = now.getMonth(); // 0-based

  // If monthly targets exist, use them
  if (targets && targets.monthlyTargets && Array.isArray(targets.monthlyTargets)) {
    // Prorate to the day of the month the actuals stop at — the selected date —
    // so the target covers the same span the MTD figure does. Matches how the
    // sales scoreboards define month-to-date.
    const monthlyTarget = getMonthlyTarget(targets, field, currentMonth);
    const daysInMonth = new Date(now.getFullYear(), currentMonth + 1, 0).getDate();
    const daysElapsed = Math.min(now.getDate(), daysInMonth);
    return Math.round(monthlyTarget * (daysElapsed / daysInMonth));
  }

  // Fallback to old frequency-based calculation
  return convertTargetValue(targets, field, getFiscalMetaForDealership(dealership));
}

// Compute metrics from raw data
function computeMetrics(apparelEntries, partsEntries, serviceEntries, targets, dealership, selectedDate) {
  const now = new Date(selectedDate);
  // Reference period from the selected date (local, what the user means);
  // row membership from each row's UTC calendar date, matching Overview,
  // the Fixed Ops dashboard and Profit Summary.
  const monthKey = localMonthKey(now);
  const throughKey = localDateKey(now);

  // MTD: from the start of the selected month through the selected date,
  // inclusive — the rest of the month is excluded even when it holds data, so
  // the figure spans the same days the prorated target does.
  const isMTD = (d) => utcMonthKey(d) === monthKey && utcDateKey(d) <= throughKey;

  const apparelMTD = apparelEntries.filter(e => isMTD(e.date));
  const partsMTD = partsEntries.filter(e => isMTD(e.date));
  const serviceMTD = serviceEntries.filter(e => isMTD(e.date));

  // Helper to sum a field
  const sumField = (list, field) => list.reduce((sum, i) => sum + (Number(i[field]) || 0), 0);

  // Apparel Metrics
  const apparelGrossMTD = sumField(apparelMTD, 'grossProfit');
  const apparelRevenueMTD = sumField(apparelMTD, 'netSales');
  const apparelTransMTD = sumField(apparelMTD, 'transactions');

  // Parts Metrics
  const partsGrossMTD = sumField(partsMTD, 'grossProfit');
  const partsRevenueMTD = sumField(partsMTD, 'netSales');
  const partsTransMTD = sumField(partsMTD, 'transactions');

  // Service Metrics
  const serviceGrossMTD = sumField(serviceMTD, 'grossProfit');
  const serviceRevenueMTD = sumField(serviceMTD, 'netSales');
  const serviceHoursMTD = sumField(serviceMTD, 'hours');
  // Each row is one day's service entry carrying that day's RO count, so sum the
  // counts — the row count is days entered, not repair orders.
  const serviceROsMTD = sumField(serviceMTD, 'repairOrders');

  // Total Fixed Ops
  const totalGrossMTD = apparelGrossMTD + partsGrossMTD + serviceGrossMTD;
  const totalRevenueMTD = apparelRevenueMTD + partsRevenueMTD + serviceRevenueMTD;

  // Get targets
  const apparelGrossTargetMTD = getMTDTarget(targets, 'apparelGrossProfit', dealership, selectedDate);
  const apparelRevenueTargetMTD = getMTDTarget(targets, 'apparelNetSales', dealership, selectedDate);
  const apparelTransTargetMTD = getMTDTarget(targets, 'apparelTransactions', dealership, selectedDate);

  const partsGrossTargetMTD = getMTDTarget(targets, 'partsGrossProfit', dealership, selectedDate);
  const partsRevenueTargetMTD = getMTDTarget(targets, 'partsNetSales', dealership, selectedDate);
  const partsTransTargetMTD = getMTDTarget(targets, 'partsTransactions', dealership, selectedDate);

  const serviceGrossTargetMTD = getMTDTarget(targets, 'serviceGrossProfit', dealership, selectedDate);
  const serviceRevenueTargetMTD = getMTDTarget(targets, 'serviceNetSales', dealership, selectedDate);
  const serviceHoursTargetMTD = getMTDTarget(targets, 'serviceHours', dealership, selectedDate);
  const serviceROsTargetMTD = getMTDTarget(targets, 'serviceTransactions', dealership, selectedDate);

  const totalGrossTargetMTD = apparelGrossTargetMTD + partsGrossTargetMTD + serviceGrossTargetMTD;
  const totalRevenueTargetMTD = apparelRevenueTargetMTD + partsRevenueTargetMTD + serviceRevenueTargetMTD;

  return {
    // Apparel
    apparelGross: { monthly: apparelGrossMTD, monthlyTarget: apparelGrossTargetMTD },
    apparelRevenue: { monthly: apparelRevenueMTD, monthlyTarget: apparelRevenueTargetMTD },
    apparelTrans: { monthly: apparelTransMTD, monthlyTarget: apparelTransTargetMTD },
    // Parts
    partsGross: { monthly: partsGrossMTD, monthlyTarget: partsGrossTargetMTD },
    partsRevenue: { monthly: partsRevenueMTD, monthlyTarget: partsRevenueTargetMTD },
    partsTrans: { monthly: partsTransMTD, monthlyTarget: partsTransTargetMTD },
    // Service
    serviceGross: { monthly: serviceGrossMTD, monthlyTarget: serviceGrossTargetMTD },
    serviceRevenue: { monthly: serviceRevenueMTD, monthlyTarget: serviceRevenueTargetMTD },
    serviceHours: { monthly: serviceHoursMTD, monthlyTarget: serviceHoursTargetMTD },
    serviceROs: { monthly: serviceROsMTD, monthlyTarget: serviceROsTargetMTD },
    // Totals
    totalGross: { monthly: totalGrossMTD, monthlyTarget: totalGrossTargetMTD },
    totalRevenue: { monthly: totalRevenueMTD, monthlyTarget: totalRevenueTargetMTD }
  };
}

function FixedOpsScoreboard({ user, managedOwner } = {}) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState(null);
  const [selectedDealershipObj, setSelectedDealershipObj] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasTargets, setHasTargets] = useState(true);

  // Load dealerships on mount
  useEffect(() => {
    async function loadDealerships() {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // In owner / managed-owner view, scope to that owner's dealerships so a
        // site manager viewing a specific owner doesn't see (or pick) every
        // dealership on the platform. Otherwise use the caller's own access.
        const isOwnerView = user?.role === 'OWNER' || managedOwner;
        let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
        if (managedOwner && isOwnerView) {
          url += `?ownerId=${managedOwner.id}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error('Failed to load dealerships');

        const data = await res.json();
        const dsList = data.dealerships || [];
        setDealerships(dsList);

        if (dsList.length > 0) {
          setSelectedDealership(dsList[0].id);
          setSelectedDealershipObj(dsList[0]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load dealerships');
      }
    }
    loadDealerships();
  }, [user, managedOwner]);

  // Fetch metrics when dealership or date changes
  useEffect(() => {
    if (!selectedDealership || !selectedDealershipObj) return;

    async function fetchMetrics() {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [apparelRes, partsRes, serviceRes, targetsRes] = await Promise.all([
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/apparel-licensing`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/parts-sales`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/service-jobs`, { headers }),
          fetch(`${API_URL}/api/targets/${selectedDealership}`, { headers })
        ]);

        const apparelData = apparelRes.ok ? await apparelRes.json() : { entries: [] };
        const partsData = partsRes.ok ? await partsRes.json() : { entries: [] };
        const serviceData = serviceRes.ok ? await serviceRes.json() : { entries: [] };
        const targetsData = targetsRes.ok ? await targetsRes.json() : { fixedOps: {} };

        const apparelEntries = (apparelData.entries || []).map((e) => ({
          date: e.date || e.saleDate,
          grossProfit: Number(e.grossProfit) || 0,
          netSales: Number(e.netSales) || 0,
          transactions: Number(e.transactions) || 0
        }));

        const partsEntries = (partsData.entries || partsData.partsSales || []).map((p) => ({
          date: p.saleDate || p.date,
          grossProfit: Number(p.grossProfit) || 0,
          netSales: Number(p.totalRevenue || p.netSales || 0),
          transactions: Number(p.transactions) || 0
        }));

        const serviceEntries = (serviceData.entries || serviceData.serviceJobs || []).map((s) => ({
          date: s.serviceDate || s.date,
          grossProfit: Number(s.grossProfit ?? 0),
          netSales: Number(s.totalRevenue || s.netSales || 0),
          hours: Number(s.laborHours || s.hours || 0),
          // `entries` exposes the day's RO count as repairOrders; raw serviceJobs
          // rows carry it as roNumber, the string column it's stored in.
          repairOrders: Number(s.repairOrders ?? parseInt(s.roNumber, 10)) || 0
        }));

        const computedMetrics = computeMetrics(
          apparelEntries,
          partsEntries,
          serviceEntries,
          targetsData.fixedOps || {},
          selectedDealershipObj,
          selectedDate
        );

        setMetrics(computedMetrics);
        setHasTargets(Array.isArray(targetsData.fixedOps?.monthlyTargets) && targetsData.fixedOps.monthlyTargets.length > 0);
      } catch (err) {
        setError(err.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, [selectedDealership, selectedDealershipObj, selectedDate]);

  const handleDealershipChange = (dealershipId) => {
    setSelectedDealership(dealershipId);
    const dealershipObj = dealerships.find(d => d.id === dealershipId);
    setSelectedDealershipObj(dealershipObj || null);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.mozFullScreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreen = () => {
    const scoreboardElement = document.querySelector('.fixedops-scoreboard');
    if (!fullscreen && scoreboardElement) {
      if (scoreboardElement.requestFullscreen) {
        scoreboardElement.requestFullscreen();
      } else if (scoreboardElement.webkitRequestFullscreen) {
        scoreboardElement.webkitRequestFullscreen();
      } else if (scoreboardElement.mozRequestFullScreen) {
        scoreboardElement.mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    }
  };

  const percentClass = (pct) => {
    if (pct >= 80) return 'high';
    if (pct >= 50) return 'mid';
    return 'low';
  };

  const getTrendIndicator = (pct) => {
    if (pct >= 80) return { label: 'On Track', class: 'trend-on-track' };
    if (pct >= 50) return { label: 'Caution', class: 'trend-caution' };
    return { label: 'Behind', class: 'trend-behind' };
  };

  if (loading) return <div className="scoreboard-loading">Loading...</div>;
  if (error) return <div className="scoreboard-error">{error}</div>;
  if (!dealerships.length) return <div className="scoreboard-error">No dealerships found.</div>;
  if (!metrics) return <div className="scoreboard-loading">Loading metrics...</div>;
  if (!hasTargets) return <NoTargetsPlaceholder />;

  const m = metrics;

  // Helper to render a metric card. MTD only: entries are posted at end of day,
  // so a daily column reads blank for most of the day.
  const renderMetricCard = (label, metric, formatter = (v) => v) => {
    const monthlyPct = calculatePercent(metric.monthly, metric.monthlyTarget);
    const monthlyTrend = getTrendIndicator(monthlyPct);

    return (
      <div className="kpi-card">
        <div className="kpi-label">{label}</div>
        <div className="kpi-compare-multi">
          <div className="kpi-period-block">
            <div className="kpi-sub">MTD</div>
            <div className="kpi-value">{formatter(metric.monthly)}</div>
            <div className="kpi-target">Target: {formatter(metric.monthlyTarget)}</div>
            <div className={`kpi-percent ${percentClass(monthlyPct)}`}>{monthlyPct}%</div>
            <div className="kpi-meter-bar">
              <div className={`kpi-meter-fill ${percentClass(monthlyPct)}`} style={{ width: `${Math.min(monthlyPct, 100)}%` }}></div>
            </div>
            <div className={`trend-indicator ${monthlyTrend.class}`}>{monthlyTrend.label}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixedops-scoreboard">
      <div className="scoreboard-header">
        <h1><span style={{ color: '#ff8c00', fontSize: '1.2em' }}>{dealerships.find(d => d.id === selectedDealership)?.name || ''}: Fixed Operations Scoreboard</span></h1>
        <div className="scoreboard-controls">
          <ScoreboardDropdown
            options={dealerships.map(d => ({ value: d.id, label: d.name }))}
            value={selectedDealership}
            onChange={handleDealershipChange}
          />
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
          <button className="fullscreen-btn" onClick={handleFullscreen}>
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>
      <div className="scoreboard-main-content">
        <div className="scoreboard-metrics-container full-width">
          {/* Group 1: Service Metrics */}
          <div className="metric-group">
            <h3 className="metric-group-title">Service</h3>
            <div className="kpi-grid">
              {renderMetricCard('Service Gross Profit', m.serviceGross, formatCurrency)}
              {renderMetricCard('Service Revenue', m.serviceRevenue, formatCurrency)}
              {renderMetricCard('Labor Hours', m.serviceHours, formatNumber)}
              {renderMetricCard('Repair Orders', m.serviceROs, formatNumber)}
            </div>
          </div>

          {/* Group 2: Parts Metrics */}
          <div className="metric-group">
            <h3 className="metric-group-title">Parts</h3>
            <div className="kpi-grid">
              {renderMetricCard('Parts Gross Profit', m.partsGross, formatCurrency)}
              {renderMetricCard('Parts Revenue', m.partsRevenue, formatCurrency)}
              {renderMetricCard('Parts Transactions', m.partsTrans, formatNumber)}
            </div>
          </div>

          {/* Group 3: Apparel Metrics */}
          <div className="metric-group">
            <h3 className="metric-group-title">Apparel & Licensing</h3>
            <div className="kpi-grid">
              {renderMetricCard('Apparel Gross Profit', m.apparelGross, formatCurrency)}
              {renderMetricCard('Apparel Revenue', m.apparelRevenue, formatCurrency)}
              {renderMetricCard('Apparel Transactions', m.apparelTrans, formatNumber)}
            </div>
          </div>

          {/* Group 4: Total Fixed Ops */}
          <div className="metric-group">
            <h3 className="metric-group-title">Total Fixed Operations</h3>
            <div className="kpi-grid">
              {renderMetricCard('Total Gross Profit', m.totalGross, formatCurrency)}
              {renderMetricCard('Total Revenue', m.totalRevenue, formatCurrency)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FixedOpsScoreboard;
